import { SortingRule } from "react-table";
import { SelectItemsProps } from "../components/ClientEquipmentBidirecional/ClientEquipmentBidirecional";
import { NavbarTitles } from "../components/Layout/NavbarItems";
import { AuthDTO } from "../models/AuthDTO";
import { Parameter } from "../models/Parameter";
import { RoleListCheckbox } from "../models/User/RoleListCheckboxDTO";
import { AssistanceService } from "../services/AssistanceService";
import { PageURL } from "./PageURL";
import RoutesObject from "./Routes";
import Toast from "./Toast";

class UtilitiesClass {

    protected assistanceService = new AssistanceService();

    //Método para verificar se valor é inteiro
    // Safe integer em javascript (+/- 2^53)
    public IsInteger(value: any): boolean {
        return Number.isInteger(value) && Number.isSafeInteger(value);
    }

    //Método para verificar se valor é data
    public IsDate(value: any): boolean {
        switch (typeof value) {
            case 'number':
                return true;
            case 'string':
                return !isNaN(Date.parse(value));
            case 'object':
                if (value instanceof Date) {
                    return !isNaN(value.getTime());
                } else {
                    return false;
                }
            default:
                return false;
        }
    }

    //Método para retonar valor default se undefined ou a null
    public IfIsUndefinedOrNullThen(value: any, defaultValue: any) {
        return value === undefined || value === null ? defaultValue : value;
    }

    //Método para adicionar um query parameter ao URL
    public AddParamToQueryIfValue(queryParamName: string, type: "string" | "number" | "date", value: any): string {
        if (value == null || value === "" || (type === "date" && !this.IsDate(value)) || (type === "number" && !this.IsInteger(value))) return '';

        return `&${queryParamName}=${value}`;
    }

    //Método para obter um parâmetro dos parâmetros do URL convertido já como o valor correto    
    public LoadParameterFromURLQuery(parameterName: string, validateParameterType: "string" | "date" | "number" | "boolean", defaultValue: any): any {
        try {
            const searchParams = new URLSearchParams(PageURL.GetSearchParameters());
            const parameterExist = searchParams.get(parameterName);

            if (parameterExist === null) return defaultValue;

            switch (validateParameterType) {
                case "date":
                    return this.IsDate(parameterExist) ? parameterExist : defaultValue;

                case "number":
                    return this.IsInteger(parameterExist) ? parseInt(parameterExist) : defaultValue;

                case "string":
                    return (parameterExist && parameterExist.trim().length > 0) ? parameterExist.toString() : defaultValue;

                case "boolean":
                    return (parameterExist === "true") ? true : false;
            }

        } catch (exp) {
            return defaultValue;
        }
    }

    //Método para carregar o tamanho da página pelo URL
    public LoadPageSizeFromURLQuery(pageSizes: number[], defaultPageSize: number = 5): number {
        const searchParams = new URLSearchParams(PageURL.GetSearchParameters());
        const tmpPageSize = searchParams.get("pageSize");
        const futurePageSize = tmpPageSize ? parseInt(tmpPageSize) : defaultPageSize;
        return pageSizes.includes(futurePageSize) ? futurePageSize : pageSizes[0];
    }

    // Método para obter o tamanho da página das configurações
    public GetPageSizeFromSettings(pageSizes: number[], pageSizeOptions: string | undefined): number[] {
        // Se não houver configuração, retorna os tamanhos padrão
        if (!pageSizeOptions) {
            return pageSizes;
        }
        
        // Tenta converter o valor das configurações para número
        const pageSizeValue = parseInt(pageSizeOptions);
        
        // Se o valor for inválido, retorna os tamanhos padrão
        if (isNaN(pageSizeValue) || pageSizeValue <= 0) {
            return pageSizes;
        }
        
        // Se o valor já estiver incluído nos tamanhos padrão, retorna os tamanhos padrão
        if (pageSizes.includes(pageSizeValue)) {
            return pageSizes;
        }
        
        // Adiciona o valor das configurações ao array, mantendo a ordem
        const newPageSizes = [...pageSizes, pageSizeValue].sort((a, b) => a - b);
        return newPageSizes;
    }

    // Método para obter o tamanho inicial da página com base nas configurações
    public GetInitialPageSize(pageSizes: number[], pageSizeOptions: string | undefined, defaultSize: number = 5): number {
        if (!pageSizeOptions) {
            return defaultSize;
        }
        
        const pageSizeValue = parseInt(pageSizeOptions);
        
        if (isNaN(pageSizeValue) || pageSizeValue <= 0) {
            return defaultSize;
        }
        
        return pageSizeValue;
    }

    // Método para carregar a página do URL
    public LoadPageFromURLQuery(pageParamName: string): number {
        const searchParams = new URLSearchParams(PageURL.GetSearchParameters());
        const tmpPage = searchParams.get(pageParamName) ?? '1';
        const futurePage = parseInt(tmpPage);

        return futurePage <= 0 ? 1 : futurePage;
    }

    public LoadSortParametersFromURLQuery(sortFieldName: string, sortOrderName: string): SortingRule<object>[] {
        try {
            const sortField = this.LoadParameterFromURLQuery(sortFieldName, "string", null);
            if (sortField === null) return [];

            const sortOrder = this.LoadParameterFromURLQuery(sortOrderName, "string", "ASC");

            return [{ id: sortField, desc: sortOrder === "DESC" }];
        } catch (exp) {
            return [];
        }
    }

    public RolesToDisable(rolesList: RoleListCheckbox[], selectedRoleId: string): string[] {
        const rolesThatCantExistAtSameTime: string[] = ["TÉCNICO INTERNO", "TÉCNICO SAT", "AGENTE", "CENTRAL DE GARANTIA"];
        const roleToChange = rolesList.find(r => r.id === selectedRoleId);

        if (roleToChange && rolesThatCantExistAtSameTime.includes(roleToChange.name.toUpperCase())) {
            return roleToChange.isChecked ? [] : rolesThatCantExistAtSameTime.filter(r => r !== roleToChange.name.toUpperCase());
        }

        return rolesList.filter(r => r.isDisabled).map(r => r.name.toUpperCase());
    }

    public IsNullOrEmpty = (value: string | null | undefined): boolean => {
        try {
            if (typeof value == "undefined") return true;
            if (value == null) return true;
            if (typeof value !== "string") return true;
            if (value.trim().length <= 0) return true;
            return false;
        } catch (exp) {
            return true;
        }
    }

    public DaysBetweenDates = (initDate: string, endDate: string): number => {
        try {
            if (this.IsNullOrEmpty(initDate) || this.IsNullOrEmpty(endDate)) return 0;

            var firstDate = Date.parse(initDate);
            var secondDate = Date.parse(endDate);

            if (isNaN(firstDate) || isNaN(secondDate)) return 0;

            var differenceInMs = secondDate - firstDate;
            var differenceInDays = Math.round(differenceInMs / (86400000));

            return differenceInDays;
        } catch (exp) {
            return 0;
        }
    }

    public MountSortParameters = (sortBy: SortingRule<object>[] | null): Parameter[] => {
        var parameters: Parameter[] = [];

        if (sortBy == null) return parameters;

        if (sortBy.length > 0) {
            parameters.push(new Parameter(sortBy[0].id, sortBy[0].desc === true ? "DESC" : "ASC"));
        }

        return parameters;
    }

    public CreateSearchURL(parameters: string[]): string {
        return "?" + parameters.filter(param => !this.IsNullOrEmpty(param))
            .map((param, index) => index === 0 ? (param.startsWith("&") ? param.replace("&", "") : param) : (param.startsWith("&") ? param : "&" + param))
            .join("");
    }

    public formatDate(date: Date) {
        // Alterar método uma vez que estava a passar 31-05-2024 e ele metia dia 30-05-2024
        // Metia um dia a menos da data recebida
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        return `${day}-${month}-${year}`;
    }

    public GetTimeFromDateTime(dateTime: Date): string {
        const hours: string = dateTime.getHours().toString().padStart(2, '0');
        const minutes: string = dateTime.getMinutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    public MapViewToRoute(view: string, IsActiveMaintenanceModule: boolean, showMaintenancePlanAfterLogin: boolean) {
        switch (view.toUpperCase()) {
            case "OFICINA":
                return '/';
            case "BACKOFFICE":
                if (IsActiveMaintenanceModule && showMaintenancePlanAfterLogin) {
                    return (RoutesObject.maintenancePlans.list);
                }
                return (RoutesObject.backoffice.list)
            default:
                return (RoutesObject.oficina.list);
        }
    }

    public processValue = (value: any) => {
        return JSON.stringify(value, null, 2)
            .replaceAll("[", "")
            .replaceAll(",", ",\n")
            .replaceAll("]", "")
            .replaceAll(/\\"/g, '"')
            .replaceAll(/\\\\/g, "");
    };

    /**
 * A function that formats an assistance document.
 *
 * @param {any} docType - the type of the document
 * @param {any} serie - the series of the document
 * @param {any} number - the number of the document
 * @return {any} the formatted assistance document
 */
    public FormattedAssistanceDocument = (docType: any, serie: any, number: any): any => {
        return docType + " " + serie + " " + number;
    };
    public ShowParentOption = (navbarTitle: NavbarTitles, currentUser: AuthDTO | null, availableModules: string[]) => {
        if (navbarTitle.navbarItems.findIndex(a => a.IsVisible(currentUser) === true) === -1) return false;

        var hasAnyModule: boolean = false;

        if (navbarTitle.navbarItems.length === 1) {
            return true;
        }

        navbarTitle.navbarItems.forEach(item => {
            //Se nenhuma das opção "filho" estiverem ativas, então não mostra a opção "pai"
            if (typeof item.moduleName !== 'undefined' && item.moduleName !== null && item.moduleName !== "") {
                if (this.HasModule(item.moduleName, availableModules)) {
                    hasAnyModule = true;
                }
            }
            else {
                hasAnyModule = true;
            }
        })

        return hasAnyModule;
    }

    public HasModule = (moduleName: string, modules: string[]) => {
        return modules?.findIndex(a => a.toUpperCase() === moduleName.toUpperCase()) !== -1;
    }

    public calculateTotalPrice = (quantity: number, sellingPrice: number): number => {
        return quantity * sellingPrice;
    }

    public formatDateYYYYMMDD(date: Date) {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    public CombineDateAndTime = (date: Date, time: string): Date => {
        if (!this.IsDate(date) || this.IsNullOrEmpty(time)) return date;
    
        const [hours, minutes] = time.split(":").map(Number);
    
        const combined = new Date(date);
        combined.setHours(hours);
        combined.setMinutes(minutes);
        combined.setSeconds(0);
        combined.setMilliseconds(0);
        combined.setMinutes(combined.getMinutes() - combined.getTimezoneOffset());
    
        return combined;
    };

    public formatDateTimeDDMMYYYYHHMM(date: Date | string | null | undefined): string {
        if (!date) return "";
    
        const d = new Date(date);
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear();
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
    
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    public readonly PasswordRegularExpression = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).*$');

    // Passamos os módulos e o último index fixo conehcido, para ele fazer o cálculo
    // Ver ClientNavBar --» primeiro sitio a ser usado
    public GetDynamicTabIndices(availableModules: string[], baseIndex: number): { module: string, index: number }[] {
        const indices = [];
        let currentIndex = baseIndex;

        if (availableModules.includes("ProjectSage50cIsActiveProjectModule")) {
            indices.push({ module: "ProjectSage50cIsActiveProjectModule", index: currentIndex });
            currentIndex++;
        }

        if (availableModules.includes("ProfitabilitySage50cIsActiveProfitabilityModule")) {
            indices.push({ module: "ProfitabilitySage50cIsActiveProfitabilityModule", index: currentIndex });
            currentIndex++;
        }

        if (availableModules.includes("IsActiveWarrantyModule")) {
            indices.push({ module: "IsActiveWarrantyModule", index: currentIndex });
            currentIndex++;
        }

        if (availableModules.includes("IsActiveMaintenanceModule")) {
            indices.push({ module: "IsActiveMaintenanceModule", index: currentIndex });
        }

        if (availableModules.includes("IsActiveScheduler")) {
            indices.push({ module: "IsActiveScheduler", index: currentIndex });
        }

        return indices;
    }

    public CalculateDayOfWeek = (dateString: string): string => {
        const daysOfWeek = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
        const date = new Date(dateString);
        return daysOfWeek[date.getUTCDay()];
    }

    // método para se valor for === "" | null | undefined, para props nao validadas pelo novo componente Valitarions.tsx (JOI)
    // Exemplo: se escrevermos e limparmos componente a logica "valor ?? null", nao manda null mas sim "", dando erro 400 Bad Rquest, não chegando à API
    public HandleEmptyValues = (value: any) => {
        return (value === "" || value === undefined || value == null) ? null : value;
    };

    public GetTechnicians = async (): Promise<SelectItemsProps[]> => {
        const list: SelectItemsProps[] = [];
        var response = await this.assistanceService.GetWorkshopTechniciansWithCode();

        if (response.success === false) {
            Toast.Show("error", response.message);
            return [];
        }

        response.obj.forEach((element: any) => {
            var item: SelectItemsProps = {
                value: element.id,
                label: element.name,
            };
            list.push(item);
        });

        return list;
    };

}

export const Utilities = new UtilitiesClass();